import { type KeyboardEvent, type ReactNode } from 'react';
import { useActiveTabVisibility } from './useActiveTabVisibility';

interface SectionTab<T extends string> {
  id: T;
  label: string;
  icon?: ReactNode;
}

/** Flat secondary navigation inside a workspace; never another card or accordion. */
export function SectionTabs<T extends string>({ id, label, tabs, value, onChange, className = '' }: {
  id: string;
  label: string;
  tabs: readonly SectionTab<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
}) {
  const rail = useActiveTabVisibility(value);
  function navigate(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1
      : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    event.currentTarget.closest('[role="tablist"]')?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus();
    const nextTab = tabs[next];
    if (nextTab) onChange(nextTab.id);
  }

  return <div ref={rail} className={`section-tabs${className ? ` ${className}` : ''}`} role="tablist" aria-label={label}>
    {tabs.map((tab, index) => <button key={tab.id} id={`${id}-tab-${tab.id}`} type="button" role="tab"
      aria-selected={value === tab.id} aria-controls={`${id}-panel-${tab.id}`} tabIndex={value === tab.id ? 0 : -1}
      onClick={() => onChange(tab.id)} onKeyDown={(event) => navigate(event, index)}>{tab.icon}{tab.label}</button>)}
  </div>;
}
